import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  getConversations, 
  getThemes, 
  getTags, 
  getFavorites, 
  getSettings,
  getViewHistory,
  saveConversations,
  saveThemes,
  saveTags,
  saveFavorites,
  saveSettings,
  saveViewHistory,
  initializeEmptyData,
  clearAllData as storageClearAllData
} from '../utils/storage';


// データコンテキストの作成
const DataContext = createContext();

// データプロバイダーコンポーネント
export const DataProvider = ({ children }) => {
  // 状態の初期化
  const [conversations, setConversations] = useState([]);
  const [themes, setThemes] = useState([]);
  const [tags, setTags] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [viewHistory, setViewHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 初期データの読み込み
  useEffect(() => {
    const loadInitialData = () => {
      try {
        setIsLoading(true);
        
        // ローカルストレージからデータを読み込む
        let loadedConversations = getConversations();
        let loadedThemes = getThemes();
        let loadedTags = getTags();
        let loadedFavorites = getFavorites();
        let loadedViewHistory = getViewHistory();
        let loadedSettings = getSettings();
        
        // ローカルストレージにデータがない場合、空のデータで初期化
        if (!loadedConversations || loadedConversations.length === 0) {
          console.log('データが見つからないため、空の状態で初期化します。');
          initializeEmptyData();
          // 初期化後のデータを再読み込み
          loadedConversations = getConversations();
          loadedThemes = getThemes();
          loadedTags = getTags();
          loadedFavorites = getFavorites();
          loadedViewHistory = getViewHistory();
          loadedSettings = getSettings();
        } else {
          console.log('ローカルストレージからデータを正常にロードしました。');
        }
        
        // 状態を更新
        setConversations(loadedConversations || []);
        setThemes(loadedThemes || []);
        setTags(loadedTags || []);
        setFavorites(loadedFavorites || []);
        setViewHistory(loadedViewHistory || []);
        setSettings(loadedSettings || {
          theme: 'light',
          language: 'ja',
          reminderEnabled: true,
          defaultView: 'timeline'
        });
        setError(null);
      } catch (err) {
        console.error('データの読み込み中にエラーが発生しました:', err);
        setError('データの読み込みに失敗しました');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitialData();
  }, []);

  // 会話の追加
  const addConversation = (newConversation) => {
    setConversations(prev => {
      const updated = [...prev, newConversation];
      saveConversations(updated);
      return updated;
    });
  };

  // 会話の更新
  const updateConversation = (updatedConversation) => {
    setConversations(prev => {
      const updated = prev.map(conv => 
        conv.id === updatedConversation.id ? updatedConversation : conv
      );
      saveConversations(updated);
      return updated;
    });
  };

  // 会話の削除
  const deleteConversation = (conversationId) => {
    setConversations(prev => {
      const updated = prev.filter(conv => conv.id !== conversationId);
      saveConversations(updated);
      return updated;
    });
    
    // 関連するお気に入りも削除
    setFavorites(prev => {
      const updated = prev.filter(id => id !== conversationId);
      saveFavorites(updated);
      return updated;
    });
  };

  // テーマの追加
  const addTheme = (newTheme) => {
    setThemes(prev => {
      const updated = [...prev, newTheme];
      saveThemes(updated);
      return updated;
    });
  };

  // テーマの更新
  const updateTheme = (updatedTheme) => {
    setThemes(prev => {
      const updated = prev.map(theme => 
        theme.id === updatedTheme.id ? updatedTheme : theme
      );
      saveThemes(updated);
      return updated;
    });
  };

  // テーマの削除
  const deleteTheme = (themeId) => {
    setThemes(prev => {
      const updated = prev.filter(theme => theme.id !== themeId);
      saveThemes(updated);
      return updated;
    });
  };

  // タグの追加
  const addTag = (newTag) => {
    setTags(prev => {
      const updated = [...prev, newTag];
      saveTags(updated);
      return updated;
    });
  };

  // タグの更新
  const updateTag = (updatedTag) => {
    setTags(prev => {
      const updated = prev.map(tag => 
        tag.id === updatedTag.id ? updatedTag : tag
      );
      saveTags(updated);
      return updated;
    });
  };

  // タグの削除
  const deleteTag = (tagId) => {
    setTags(prev => {
      const updated = prev.filter(tag => tag.id !== tagId);
      saveTags(updated);
      return updated;
    });
  };

  // お気に入りの追加/削除
  const toggleFavorite = (conversationId) => {
    setFavorites(prev => {
      let updated;
      if (prev.includes(conversationId)) {
        updated = prev.filter(id => id !== conversationId);
      } else {
        updated = [...prev, conversationId];
      }
      saveFavorites(updated);
      return updated;
    });
  };

  // 会話の閲覧履歴を記録
  const recordView = (conversationId) => {
    if (!conversationId) return;
    
    const newViewEntry = {
      id: conversationId,
      timestamp: new Date().toISOString()
    };
    
    setViewHistory(prev => {
      const updated = [...prev, newViewEntry];
      saveViewHistory(updated);
      return updated;
    });
  };

  // 閲覧履歴をクリア
  const clearViewHistory = () => {
    setViewHistory([]);
    saveViewHistory([]);
  };

  // 設定の更新
  const updateSettings = (newSettings) => {
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      saveSettings(updated);
      return updated;
    });
  };

  // すべてのデータを削除
  const clearAllData = () => {
    try {
      setIsLoading(true);
      
      // ストレージからすべてのデータを削除
      const result = storageClearAllData();
      
      if (result) {
        // 空のデータを初期化
        initializeEmptyData();
        
        // 状態をリセット
        setConversations([]);
        setThemes([]);
        setTags([]);
        setFavorites([]);
        setViewHistory([]);
        setSettings({
          theme: 'light',
          language: 'ja',
          reminderEnabled: true,
          defaultView: 'timeline'
        });
        setError(null);
        return true;
      } else {
        setError('データの削除に失敗しました');
        return false;
      }
    } catch (err) {
      console.error('データの削除中にエラーが発生しました:', err);
      setError('データの削除に失敗しました');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // インポートしたデータで状態を更新
  const refreshData = () => {
    try {
      setIsLoading(true);
      
      // ローカルストレージからデータを読み込む
      const loadedConversations = getConversations();
      const loadedThemes = getThemes();
      const loadedTags = getTags();
      const loadedFavorites = getFavorites();
      const loadedViewHistory = getViewHistory();
      const loadedSettings = getSettings();
      
      // データがない場合は空のデータを初期化
      if (!loadedConversations && !loadedThemes && !loadedTags) {
        console.log('データが見つからないため、空のデータを初期化します');
        initializeEmptyData();
      }
      
      // 状態を更新
      setConversations(loadedConversations || []);
      setThemes(loadedThemes || []);
      setTags(loadedTags || []);
      setFavorites(loadedFavorites || []);
      setViewHistory(loadedViewHistory || []);
      setSettings(loadedSettings || {
        theme: 'light',
        language: 'ja',
        reminderEnabled: true,
        defaultView: 'timeline'
      });
      setError(null);
    } catch (err) {
      console.error('データの読み込み中にエラーが発生しました:', err);
      setError('データの読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // コンテキスト値の作成
  const contextValue = {
    conversations,
    themes,
    tags,
    favorites,
    viewHistory,
    settings,
    isLoading,
    error,
    addConversation,
    updateConversation,
    deleteConversation,
    addTheme,
    updateTheme,
    deleteTheme,
    addTag,
    updateTag,
    deleteTag,
    toggleFavorite,
    recordView,
    clearViewHistory,
    updateSettings,
    clearAllData,
    refreshData
  };

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
};

// カスタムフック - データコンテキストを使用するためのフック
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export default DataContext;
